/**
 * Background printer auto-reconnect.
 *
 * When the saved printer has been unreachable continuously for a grace period
 * (~2 min — brief blips resolve on their own), this loop silently attempts to
 * reconnect with capped exponential backoff, indefinitely, until the printer is
 * reachable again (it may be powered back on hours later). On success the store
 * updates and the "printer needs attention" indicators clear on their own.
 *
 * Safety:
 *   - Gated on a real thermal-printing backend existing at all
 *     (`printerHealthTrackingApplies()`) and on the business printing policy
 *     (the `enabled` arg — wire it to `usePrintingEnabled()`).
 *   - Attempts go through `checkPrinterReadiness({ reconnect: true })`, which
 *     keeps every existing guard: printing-enabled gate, silent Bluetooth
 *     authorization check (never prompts — attempts stay radio-silent while
 *     unauthorized), single-flight, and the printer lock. A racing attempt can
 *     only queue behind an in-flight print, never corrupt it.
 *   - Only runs while the printer is *unreachable* (offline / never probed) —
 *     never for reachable-but-degraded states like paper empty, which a
 *     reconnect can't fix.
 *   - One recursive setTimeout; paused while the app is backgrounded, with a
 *     prompt attempt on return to foreground once the grace period is served.
 */
import { useEffect } from 'react';
import { App } from '@capacitor/app';

import { usePrinterStore } from 'src/store/PrinterStore';

import { checkPrinterReadiness } from './printer-readiness';
import { printerHealthTrackingApplies } from './print-routing';
import {
  withJitter,
  getBackoffDelayMs,
  RECONNECT_GRACE_MS,
  isPrinterUnreachable,
  RECONNECT_RESUME_DELAY_MS,
  RECONNECT_PRINTING_DEFER_MS,
} from './reconnect-backoff';

export function usePrinterAutoReconnect(enabled: boolean): void {
  useEffect(() => {
    // A capability, not a platform: there is nothing to reconnect to on a
    // runtime whose printing is simulated (SITE_TRIAGE bucket B1).
    if (!enabled || !printerHealthTrackingApplies()) return undefined;

    let cancelled = false;
    let appActive = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    /** When the printer became unreachable, or null while it's reachable. */
    let offlineSince: number | null = null;
    let failedAttempts = 0;
    /** Bumped on every tracking reset, so an attempt that resumes after
     *  `await` can tell its tracking session was reset under it. */
    let session = 0;
    let prevPrinterId = usePrinterStore.getState().activePrinter?.identifier ?? null;
    let removeAppListener: (() => void) | undefined;

    const clearTimer = () => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    };

    const stopTracking = (reason: string) => {
      if (offlineSince !== null) {
        console.log(`[PrinterAutoReconnect] stopped: ${reason}`);
      }
      session += 1;
      offlineSince = null;
      failedAttempts = 0;
      clearTimer();
    };

    const scheduleNext = (delayMs: number) => {
      clearTimer();
      timer = setTimeout(() => {
        runAttempt();
      }, delayMs);
    };

    const runAttempt = async () => {
      if (cancelled || offlineSince === null || !appActive) return;
      const store = usePrinterStore.getState();
      // Never contend with an active print — re-check shortly, without
      // consuming a backoff step.
      if (store.currentlyPrinting) {
        scheduleNext(RECONNECT_PRINTING_DEFER_MS);
        return;
      }
      // Another path (manual reconnect, live status event) may have fixed it.
      if (!isPrinterUnreachable(store)) {
        stopTracking('printer reachable again');
        return;
      }

      const attempt = failedAttempts + 1;
      const mySession = session;
      console.log(`[PrinterAutoReconnect] attempt ${attempt}`);
      try {
        await checkPrinterReadiness({ force: true, reconnect: true });
      } catch {
        /* contained — a failed attempt must never break the app */
      }
      if (cancelled) return;

      // A successful probe updates the store, which fires `evaluate` and stops
      // tracking before we resume here — so handle success regardless of
      // whether this session is still active (stopTracking is idempotent).
      const after = usePrinterStore.getState();
      if (!isPrinterUnreachable(after)) {
        if (after.isPrinterReady) after.clearLastError();
        stopTracking(`recovered after ${attempt} attempt(s)`);
        return;
      }
      // Still unreachable — but if tracking was reset under us (e.g. the user
      // switched printers mid-attempt), leave the new session's timer alone.
      if (session !== mySession) return;
      failedAttempts = attempt;
      if (appActive) scheduleNext(withJitter(getBackoffDelayMs(failedAttempts)));
    };

    /** Sync tracking state with the store; called on mount and every store change. */
    const evaluate = () => {
      if (cancelled) return;
      const store = usePrinterStore.getState();
      const printerId = store.activePrinter?.identifier ?? null;
      if (printerId !== prevPrinterId) {
        // Switched (or cleared) printers — restart tracking from scratch.
        prevPrinterId = printerId;
        stopTracking('active printer changed');
      }
      if (!isPrinterUnreachable(store)) {
        if (offlineSince !== null) stopTracking('printer reachable again');
        return;
      }
      if (offlineSince === null) {
        offlineSince = Date.now();
        failedAttempts = 0;
        console.log(
          `[PrinterAutoReconnect] printer unreachable — first attempt in ${RECONNECT_GRACE_MS / 1000}s`
        );
        if (appActive) scheduleNext(RECONNECT_GRACE_MS);
      }
    };

    const unsubscribe = usePrinterStore.subscribe(() => evaluate());

    (async () => {
      try {
        const handle = await App.addListener('appStateChange', ({ isActive }) => {
          if (cancelled) return;
          appActive = isActive;
          if (!isActive) {
            // No wakeups while backgrounded; tracking state is kept so the
            // grace period keeps counting wall-clock time.
            clearTimer();
            return;
          }
          if (offlineSince !== null) {
            const elapsed = Date.now() - offlineSince;
            scheduleNext(Math.max(RECONNECT_RESUME_DELAY_MS, RECONNECT_GRACE_MS - elapsed));
          } else {
            evaluate();
          }
        });
        removeAppListener = () => handle.remove();
      } catch {
        // appStateChange is unavailable on some platforms — ignore.
      }
    })();

    evaluate();

    return () => {
      cancelled = true;
      clearTimer();
      unsubscribe();
      removeAppListener?.();
    };
  }, [enabled]);
}
